/**
 * PostHog Event Tracking Utilities
 * Australian Telecommunications Platform
 * 
 * Provides standardised event tracking across all user journeys
 * with consistent property structure and error handling.
 */

import { useNuxtApp } from '#app';

// Event naming patterns for different journeys
export const EVENT_CATEGORIES = {
  PRODUCT: 'product',
  CHECKOUT: 'checkout', 
  ACTIVATION: 'activation',
  DASHBOARD: 'dashboard',
  VERIFICATION: 'verification',
  REGISTRATION: 'registration'
} as const;

// Standard event actions
export const EVENT_ACTIONS = {
  STARTED: 'started',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CLICKED: 'clicked',
  VIEWED: 'viewed',
  SELECTED: 'selected',
  SUBMITTED: 'submitted',
  CANCELLED: 'cancelled'
} as const;

// Platform detection helper
export const getPlatformContext = () => {
  if (typeof window === 'undefined') return 'server';
  
  const { $reactNative } = useNuxtApp();
  const isMobile = window.innerWidth <= 768;
  
  return {
    platform: $reactNative?.isApp ? 'mobile_app' : 'web',
    device_type: isMobile ? 'mobile' : 'desktop',
    is_react_native: Boolean($reactNative?.isApp),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight
  };
};

// User context helper
export const getUserContext = () => {
  try {
    const { $auth } = useNuxtApp();
    
    // Type-safe auth access with fallback
    const authUser = ($auth as any)?.user;
    
    return {
      user_type: authUser ? 'authenticated' : 'guest',
      user_id: authUser?.id || null,
      session_duration: getSessionDuration()
    };
  } catch (error) {
    // Fallback if auth is not available
    return {
      user_type: 'guest' as const,
      user_id: null,
      session_duration: getSessionDuration()
    };
  }
};

// Session duration calculation
const getSessionDuration = (): number => {
  if (typeof sessionStorage === 'undefined') return 0;
  
  const sessionStart = sessionStorage.getItem('session_start_time');
  if (!sessionStart) {
    const startTime = Date.now().toString();
    sessionStorage.setItem('session_start_time', startTime);
    return 0;
  }
  
  return Date.now() - parseInt(sessionStart, 10);
};

// Standard properties that go with every event
const getStandardProperties = (componentName: string) => {
  const platformContext = getPlatformContext();
  const userContext = getUserContext();
  
  return {
    // Component context
    component_name: componentName,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    timestamp: new Date().toISOString(),
    
    // Platform context
    viewport_width: platformContext.viewport_width,
    viewport_height: platformContext.viewport_height,
    device_type: platformContext.device_type,
    is_react_native: platformContext.is_react_native,
    
    // User context
    user_type: userContext.user_type,
    user_id: userContext.user_id,
    session_duration: userContext.session_duration,
    
    // Technical context
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    language: typeof navigator !== 'undefined' ? navigator.language : 'en-AU'
  };
};

/**
 * Core event tracking function with error handling and validation
 */
export const trackEvent = (
  eventName: string,
  customProperties: Record<string, any> = {},
  componentName: string = 'Unknown'
): void => {
  try {
    // Only track on client-side
    if (typeof window === 'undefined') return;
    
    // Check if PostHog is available
    if (!window.posthog) {
      console.warn('[PostHog] PostHog not available, skipping event:', eventName);
      return;
    }
    
    // Validate event name format
    if (!eventName || typeof eventName !== 'string') {
      console.error('[PostHog] Invalid event name:', eventName);
      return;
    }
    
    // Combine standard and custom properties
    const properties = {
      ...getStandardProperties(componentName),
      ...customProperties
    };
    
    // Remove any undefined or null values
    const cleanProperties = Object.fromEntries(
      Object.entries(properties).filter(([_, value]) => value !== undefined && value !== null)
    );
    
    // Track the event
    window.posthog.capture(eventName, cleanProperties);
    
    // Development logging
    if (process.dev) {
      console.log(`[PostHog] Event tracked: ${eventName}`, cleanProperties);
    }
    
  } catch (error) {
    console.error('[PostHog] Error tracking event:', eventName, error);
  }
};

/**
 * Product-specific tracking helpers
 */
export const trackProductEvent = {
  discovery: (context: { content_sections?: number; load_time_ms?: number }) => {
    trackEvent('product_discovery_started', context, 'HomePage');
  },
  
  view: (product: { id: number; name: string; code: string }, context: { url_format?: string; category?: string }) => {
    trackEvent('product_view_started', {
      product_id: product.id,
      product_name: product.name,
      product_code: product.code,
      product_category: context.category,
      url_format: context.url_format,
    }, 'ProductPage');
  },
  
  addToCart: (cartItem: { product: any; quantity: number }, totalItems: number) => {
    trackEvent('product_add_to_cart', {
      product_id: cartItem.product.id,
      product_name: cartItem.product.name,
      quantity: cartItem.quantity,
      total_items_in_cart: totalItems,
    }, 'ProductPage');
  },
  
  buyNow: (product: { id: number; name: string }) => {
    trackEvent('product_buy_now_clicked', {
      product_id: product.id,
      product_name: product.name,
    }, 'ProductPage');
  }
};

/**
 * Checkout-specific tracking helpers
 */
export const trackCheckoutEvent = {
  started: (cart: any[]) => {
    trackEvent('checkout_started', {
      cart_items: cart.length,
      cart_total: cart.reduce((sum, item) => sum + (item.price * item.quantity), 0),
      entry_point: 'direct_navigation',
    }, 'CheckoutIndex');
  },
  
  stepCompleted: (stepIndex: number, stepName: string, validated: boolean, timingMs: number) => {
    trackEvent('checkout_step_completed', {
      step_number: stepIndex + 1,
      step_name: stepName,
      validation_success: validated,
      validation_time_ms: timingMs,
    }, 'CheckoutMainView');
  },
  
  stepFailed: (stepIndex: number, stepName: string, errorMessage: string) => {
    trackEvent('checkout_step_failed', {
      step_number: stepIndex + 1,
      step_name: stepName,
      error_message: errorMessage,
    }, 'CheckoutMainView');
  },
  
  orderCompleted: (result: any, payload: any, processingTimeMs: number) => {
    trackEvent('checkout_order_completed', {
      order_id: result.id,
      order_total: payload.request.totalAmount,
      payment_method: payload.request.paymentMethod,
      delivery_method: payload.request.deliveryMethod,
      processing_time_ms: processingTimeMs,
      items_count: payload.request.items?.length || 0,
    }, 'CheckoutIndex');
  },
  
  orderFailed: (errorMessage: string, payload: any) => {
    trackEvent('checkout_order_failed', {
      error_message: errorMessage,
      order_total: payload.request.totalAmount,
      payment_method: payload.request.paymentMethod,
    }, 'CheckoutIndex');
  }
};

/**
 * SIM Activation tracking helpers
 */
export const trackActivationEvent = {
  started: (hasPersistedData: boolean) => {
    trackEvent('activation_started', {
      has_persisted_data: hasPersistedData,
    }, 'SimActivationIndex');
  },
  
  stepCompleted: (stepIndex: number, stepName: string, timingMs: number) => {
    trackEvent('activation_step_completed', {
      step_number: stepIndex + 1,
      step_name: stepName,
      completion_time_ms: timingMs,
    }, 'ActivateMainView');
  },
  
  simNumberEntered: (simNumber: string) => {
    trackEvent('activation_sim_number_entered', {
      sim_format: simNumber.length > 15 ? 'iccid' : 'short',
      input_method: 'manual', // Could be 'scan' in future
    }, 'ActivateMainView');
  },
  
  completed: (result: any, totalTimeMs: number) => {
    trackEvent('activation_completed', {
      order_id: result.id,
      activation_time_ms: totalTimeMs,
      cleanup_performed: true,
    }, 'SimActivationIndex');
  }
};

/**
 * Dashboard tracking helpers
 */
export const trackDashboardEvent = {
  accessed: (viewQuery: string, context: any) => {
    trackEvent('dashboard_accessed', {
      initial_view: viewQuery || 'my-plans',
      device_type: context.isMobile ? 'mobile' : 'desktop',
      is_react_native: context.isReactNative,
      from_verification: context.fromVerification,
    }, 'DashboardIndex');
  },
  
  navigationClicked: (fromView: string, toView: string) => {
    trackEvent('dashboard_navigation_clicked', {
      from_view: fromView,
      to_view: toView,
    }, 'DashboardIndex');
  },
  
  serviceSelected: (serviceId: string, serviceType: string) => {
    trackEvent('dashboard_service_selected', {
      service_id: serviceId,
      service_type: serviceType,
    }, 'DashboardMyPlans');
  },
  
  planChangeStarted: (currentPlan: string, targetPlan: string) => {
    trackEvent('dashboard_plan_change_started', {
      current_plan: currentPlan,
      target_plan: targetPlan,
    }, 'DashboardMyPlans');
  },
  
  autoRechargeToggled: (serviceId: string, enabled: boolean) => {
    trackEvent('dashboard_auto_recharge_toggled', {
      service_id: serviceId,
      auto_recharge_enabled: enabled,
    }, 'DashboardMyPlans');
  }
};

/**
 * Verification (HRT/2FA) tracking helpers
 */
export const trackVerificationEvent = {
  started: (triggerContext: string, availableMethods: string[]) => {
    trackEvent('verification_started', {
      trigger_context: triggerContext,
      available_methods: availableMethods,
      methods_count: availableMethods.length,
    }, 'VerificationPage');
  },
  
  methodSelected: (method: 'sms' | 'email', serviceId?: string, selectionTimeMs?: number) => {
    trackEvent('verification_method_selected', {
      method_type: method,
      service_id: serviceId,
      selection_time_ms: selectionTimeMs,
    }, 'VerificationPage');
  },
  
  sentSuccess: (method: string, serviceId?: string) => {
    trackEvent('verification_sent_success', {
      method_type: method,
      service_id: serviceId,
    }, 'VerificationPage');
  },
  
  sentFailed: (method: string, errorMessage: string) => {
    trackEvent('verification_sent_failed', {
      method_type: method,
      error_message: errorMessage,
    }, 'VerificationPage');
  },
  
  completed: (method: string, attempts: number, totalTimeMs: number) => {
    trackEvent('verification_completed', {
      verification_method: method,
      total_attempts: attempts,
      total_time_ms: totalTimeMs,
    }, 'VerificationPage');
  },
  
  timeout: (method: string, timeoutDurationMs: number) => {
    trackEvent('verification_timeout', {
      method_type: method,
      timeout_duration_ms: timeoutDurationMs,
    }, 'VerificationPage');
  }
};

/**
 * Error tracking helper
 */
export const trackError = (
  errorType: string,
  errorMessage: string,
  componentName: string,
  additionalContext: Record<string, any> = {}
) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    ...additionalContext,
  }, componentName);
};

/**
 * Performance tracking helper
 */
export const trackPerformance = (
  actionName: string,
  durationMs: number,
  componentName: string,
  additionalContext: Record<string, any> = {}
) => {
  trackEvent('performance_measured', {
    action_name: actionName,
    duration_ms: durationMs,
    ...additionalContext,
  }, componentName);
};

// Global type declarations for PostHog
declare global {
  interface Window {
    posthog?: {
      capture: (eventName: string, properties?: Record<string, any>) => void;
      identify: (userId: string, properties?: Record<string, any>) => void;
      reset: () => void;
    };
  }
}
